<template>
  <!-- Public mode: Show customer number lookup first -->
  <div v-if="isPublic && !customerVerified">
    <div class="p-4 mb-16">
      <div class="subheading-wrap mb-16">
        <div class="type-headline-lg">{{ $t('claims.new.headline') }}</div>
      </div>
      <div class="inputHolder">
        <div>
          <TextInput
            v-model="Customernumber"
            type="text"
            :label="$t('claims.Customernumber')"
            :readonly="false"
          />
          <div v-if="customerLookupError" class="mt-8 type-xs text-criticalDark">
            {{ customerLookupError }}
          </div>
        </div>
        <div />
      </div>
      <div class="flex mt-24">
        <button
          class="btn"
          :class="{ loading: verifyingCustomer }"
          type="button"
          @click="verifyCustomerNumber"
        >
          {{ $t('claims.verifyCustomer') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Authenticated mode: Show expand button -->
  <div v-else-if="!isPublic && !expanded" class="my-16 flex justify-center">
    <button
      class="btn"
      type="button"
      @click="expanded = true"
    >{{ $t('claims.new.headline') }}</button>
  </div>

  <!-- Main form (shown when expanded for authenticated users, or after customer verification for public) -->
  <div
    v-if="(isPublic && customerVerified) || (!isPublic && expanded)"
    class="ClaimsForm mt-16 desk:mt-0 max-h-[10000px] overflow-hidden transition-all duration-1000"
  >
    <div
      class="description-list mb-16"
      :class="{
        '!p-4': isPublic
      }"
    >
      <!-- Heading -->
      <div class="subheading-wrap mb-16">
        <h2 class="type-headline-lg">{{ $t('claims.new.headline') }}</h2>
        <!--        <button class="btn btn&#45;&#45;secondary" @click.stop.prevent="testFillRequired" type="button">Test fill required</button>-->
      </div>

      <Form
        @submit="submitForm"
        @click="startValidate"
      >
        <div class="inputHolder">
          <div>
            <TextInput
              v-model="Customernumber"
              type="text"
              :label="$t('claims.Customernumber')"
              :readonly="true"
            />
            <div class="mt-16">{{ companyName }}</div>
          </div>
        </div>

        <div class="middleInstructions">
          <h2 class="type-headline-base">{{ $t('claims.text.claimant1') }}</h2>
          <p>{{ $t('claims.text.claimant2') }}</p>
          <p>{{ $t('claims.text.claimant3') }}</p>
          <p>{{ $t('claims.text.claimant4') }}</p>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * ContactName -->
          <Field
            v-slot="{ field, meta }"
            name="ContactName"
            :rules="validateRequired"
            :model-value="ContactName"
          >
            <TextInput
              v-model="ContactName"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ContactName')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * ContactEmail -->
          <Field
            v-slot="{ field, meta }"
            name="ContactEmail"
            :rules="validateEmail"
            :model-value="ContactEmail"
          >
            <TextInput
              v-model="ContactEmail"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ContactEmail')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * ContactPhone -->
          <div>
            <TextInput
              v-model="ContactPhone"

              type="text"
              :label="$t('claims.ContactPhone')"
            />
          </div>
          <!-- * * * * * * * * * * ReportDate -->
          <Field
            v-slot="{ field, meta }"
            name="ReportDate"
            :rules="validateDate"
            :model-value="ReportDate"
          >
            <TextInput
              v-model="ReportDate"
              server-name="required"
              v-bind="field"
              type="date"
              :label="$t('claims.Date')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * OrderNumber -->
          <Field
            v-slot="{ field, meta }"
            name="OrderNumber"
            :rules="validateRequired"
            :model-value="OrderNumber"
          >
            <TextInput
              v-model="OrderNumber"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.OrderNumber')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>

          <div />
        </div>

        <div class="middleInstructions">
          <h2 class="type-headline-base">{{ $t('claims.text.installation1') }}</h2>
          <p>{{ $t('claims.text.installation2') }}</p>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * InstallationPerformedBy -->
          <div>
            <SelectDropdown
              v-model="InstallationPerformedBy"
              :label="$t('claims.InstallationPerformedBy')"
              :options="InstallationPerformedByOptions"
            />
          </div>
          <!-- * * * * * * * * * * InstallationType -->
          <div>
            <SelectDropdown
              v-model="InstallationType"
              :label="$t('claims.InstallationType')"
              :options="InstallationTypeOptions"
            />
          </div>
        </div>

        <div class="middleInstructions">
          <h2 class="type-headline-base">{{ $t('claims.text.consumer1') }}</h2>
          <p>{{ $t('claims.text.consumer2') }}</p>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * ConsumerName -->
          <Field
            v-slot="{ field, meta }"
            name="ConsumerName"
            :rules="validateRequired"
            :model-value="ConsumerName"
          >
            <TextInput
              v-model="ConsumerName"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ConsumerName')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * ConsumerAddress -->
          <Field
            v-slot="{ field, meta }"
            name="ConsumerAddress"
            :rules="validateRequired"
            :model-value="ConsumerAddress"
          >
            <TextInput
              v-model="ConsumerAddress"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ConsumerAddress')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * ConsumerPostcode -->
          <Field
            v-slot="{ field, meta }"
            name="ConsumerPostcode"
            :rules="validateRequired"
            :model-value="ConsumerPostcode"
          >
            <TextInput
              v-model="ConsumerPostcode"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ConsumerPostcode')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * ConsumerCity -->
          <Field
            v-slot="{ field, meta }"
            name="ConsumerCity"
            :rules="validateRequired"
            :model-value="ConsumerCity"
          >
            <TextInput
              v-model="ConsumerCity"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ConsumerCity')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * ConsumerCellphone -->
          <Field
            v-slot="{ field, meta }"
            name="ConsumerCellphone"
            :rules="validateRequired"
            :model-value="ConsumerCellphone"
          >
            <TextInput
              v-model="ConsumerCellphone"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ConsumerCellphone')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * ConsumerEmail -->
          <div>
            <TextInput
              v-model="ConsumerEmail"
              server-name="required"
              type="text"
              :label="$t('claims.ConsumerEmail')"
            />
          </div>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * CustomerClaimNo -->
          <div>
            <div>
              <TextInput
                v-model="CustomerClaimNo"
                server-name="required"
                type="text"
                :label="$t('claims.CustomerClaimNo')"
              />
            </div>
          </div>

          <!-- * * * * * * * * * * DateOnRecipt -->
          <div>
            <div>
              <TextInput
                v-model="DateOnRecipt"
                server-name="required"
                type="text"
                :label="$t('claims.DateOnRecipt')"
              />
            </div>
          </div>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * Receipt -->
          <div>
            <div class="type-sm text-text-subtle">{{ $t('claims.Receipt') }}</div>
            <Field
              v-slot="{ field, meta }"
              name="Receipt"
              :rules="validateRequired"
              :model-value="Receipt"
            >
              <div class="flex mt-12 gap-24">
                <label>
                  <input
                    v-model="Receipt"
                    type="radio"
                    value="true"
                  > {{ $t('claims.yes') }}
                </label>
                <label>
                  <input
                    v-model="Receipt"
                    type="radio"
                    value="false"
                  > {{ $t('claims.no') }}
                </label>
              </div>
              <div v-if="meta.errors.length" class="mt-8">
                <div
                  v-for="(error, idx) in meta.errors"
                  :key="idx"
                  class="flex flex-row items-center gap-4 whitespace-nowrap"
                >
                  <img
                    src="/icons/exclamation-circle-critical.svg"
                    class="w-16 h-16"
                    alt=""
                  >
                  <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
                </div>
              </div>
            </Field>
          </div>

          <!-- * * * * * * * * * * ConsumerDeliveryInstructions -->
          <div>
            <TextAreaInput v-model="ConsumerDeliveryInstructions" :label="$t('claims.ConsumerDeliveryInstructions')" />
          </div>
        </div>

        <div class="middleInstructions">
          <h2 class="type-headline-base">{{ $t('claims.text.defect1') }}</h2>
          <p>{{ $t('claims.text.defect2') }}</p>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * MainReasonOption -->
          <Field
            v-slot="{ field, meta }"
            name="MainReasonOption"
            :rules="validateRequired"
            :model-value="MainReasonOption"
          >
            <SelectDropdown
              v-model="MainReasonOption"
              :label="$t('claims.MainReasonOption')"
              :options="MainReasonOptions"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>

          <!-- * * * * * * * * * * ClaimError -->
          <Field
            v-slot="{ field, meta }"
            name="ClaimError"
            :rules="validateRequired"
            :model-value="ClaimError"
          >
            <TextAreaInput
              v-model="ClaimError"
              server-name="required"
              v-bind="field"
              type="text"
              :label="$t('claims.ClaimError')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="middleInstructions">
          <h2 class="type-headline-base">{{ $t('claims.text.product1') }}</h2>
          <p>{{ $t('claims.text.product2') }}</p>
          <p>{{ $t('claims.text.product3') }}</p>
          <p>{{ $t('claims.text.product4') }}</p>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * ArticleNumber -->
          <div ref="articleSearchDiv">
            <Field
              v-slot="{ field, meta }"
              name="ArticleNumber"
              :rules="validateRequired"
              :model-value="ArticleNumber"
            >
              <div class="relative">
                <label class="type-sm text-text-subtle">{{ $t('claims.ArticleNumber') }}</label>
                <input
                  v-model="ArticleNumber"
                  v-bind="field"
                  type="text"
                  class="w-full h-48 block appearance-none m-0 py-12 px-16 border type-sm text-black"
                  :class="{
                    'border-border-base': !articleSearchActive,
                    'border-darkest': articleSearchActive,
                    'border-criticalDark': meta.validated && !meta.valid
                  }"
                  :placeholder="$t('claims.ArticleNumber')"
                  @focus="articleSearchActive = true; searchArticles()"
                  @input="searchArticles"
                  @keydown.down.prevent="selectNextArticle(1)"
                  @keydown.up.prevent="selectNextArticle(-1)"
                  @keydown.enter.prevent="selectCurrentArticle"
                  @keydown.esc.prevent="closeArticleSearch"
                >

                <!-- Search results -->
                <div
                  v-if="articleSearchActive && articleSearchResults.length > 0"
                  ref="articleResultsDiv"
                  class="absolute z-above top-full left-0 w-full bg-lightest border-l border-r border-b border-darkest max-h-[240px] overflow-auto"
                >
                  <div
                    v-for="(product, index) in articleSearchResults"
                    :key="product.partNo"
                    class="flex items-center justify-between min-h-38 p-12 hover:bg-bg-subtle cursor-pointer"
                    :class="{ '!bg-bg-secondary': index === selectedArticleIndex }"
                    @click="selectArticle(product)"
                  >
                    <div class="flex flex-col gap-4">
                      <div class="type-sm">{{ product.partNo }}</div>
                      <div class="type-xs text-text-subtle">{{ product.name }}</div>
                    </div>
                  </div>
                </div>

                <!-- No results -->
                <div
                  v-if="articleSearchActive && articleSearchResults.length === 0 && ArticleNumber.length > 2 && !articleSearchLoading"
                  class="absolute z-above top-full left-0 w-full py-16 px-12 bg-lightest type-sm text-center border-l border-r border-b border-darkest"
                >
                  {{ $t('search.nohits') }}
                </div>
              </div>

              <div
                v-if="selectedProductName && ArticleNumber"
                class="mt-8 type-sm"
              >
                {{ selectedProductName }}
              </div>

              <div v-if="meta.errors.length" class="mt-8">
                <div
                  v-for="(error, idx) in meta.errors"
                  :key="idx"
                  class="flex flex-row items-center gap-4 whitespace-nowrap"
                >
                  <img
                    src="/icons/exclamation-circle-critical.svg"
                    class="w-16 h-16"
                    alt=""
                  >
                  <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
                </div>
              </div>
            </Field>
          </div>
          <div />
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * DeliveredQuantity -->
          <Field
            v-slot="{ field, meta }"
            name="DeliveredQuantity"
            :rules="validatePositiveNumber"
            :model-value="DeliveredQuantity"
          >
            <TextInput
              v-model="DeliveredQuantity"
              server-name="required"
              v-bind="field"
              type="number"
              :label="$t('claims.DeliveredQuantity')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * ClaimedQuantity -->
          <Field
            v-slot="{ field, meta }"
            name="ClaimedQuantity"
            :rules="validatePositiveNumber"
            :model-value="ClaimedQuantity"
          >
            <TextInput
              v-model="ClaimedQuantity"
              server-name="required"
              v-bind="field"
              type="number"
              :label="$t('claims.ClaimedQuantity')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * InstallationDate -->
          <Field
            v-slot="{ field, meta }"
            name="InstallationDate"
            :rules="validateDate"
            :model-value="InstallationDate"
          >
            <TextInput
              v-model="InstallationDate"
              server-name="required"
              v-bind="field"
              type="date"
              :label="$t('claims.InstallationDate')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * DefectDiscovered -->
          <Field
            v-slot="{ field, meta }"
            name="DefectDiscovered"
            :rules="validateRequired"
            :model-value="DefectDiscovered"
          >
            <TextInput
              v-model="DefectDiscovered"
              server-name="required"
              v-bind="field"
              type="date"
              :label="$t('claims.DefectDiscovered')"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * RoomType -->
          <div>
            {{ $t('claims.RoomType') }}
            <label class="checkboxLabel">
              <input
                v-model="RoomType"
                type="checkbox"
                :value="$t('claims.RT.option1')"
              > <span>{{ $t('claims.RT.option1') }}</span>
            </label>
            <label class="checkboxLabel">
              <input
                v-model="RoomType"
                type="checkbox"
                :value="$t('claims.RT.option2')"
              > <span>{{ $t('claims.RT.option2') }}</span>
            </label>
            <label class="checkboxLabel">
              <input
                v-model="RoomType"
                type="checkbox"
                :value="$t('claims.RT.option3')"
              > <span>{{ $t('claims.RT.option3') }}</span>
            </label>
            <label class="checkboxLabel">
              <input
                v-model="RoomType"
                type="checkbox"
                :value="$t('claims.RT.option4')"
              > <span>{{ $t('claims.RT.option4') }}</span>
            </label>
            <label class="checkboxLabel">
              <input
                v-model="RoomType"
                type="checkbox"
                :value="$t('claims.RT.option5')"
              > <span>{{ $t('claims.RT.option5') }}</span>
            </label>
            <label class="checkboxLabel">
              <input
                v-model="RoomType"
                type="checkbox"
                :value="$t('claims.RT.option6')"
              > <span>{{ $t('claims.RT.option6') }}</span>
            </label>
            <label class="checkboxLabel">
              <input
                v-model="RoomType"
                type="checkbox"
                :value="$t('claims.RT.option7')"
              > <span>{{ $t('claims.RT.option7') }}</span>
            </label>
          </div>

          <div />
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * FloorHeating -->
          <Field
            v-slot="{ field, meta }"
            name="FloorHeating"
            :rules="validateRequired"
            :model-value="FloorHeating"
          >
            <SelectDropdown
              v-model="FloorHeating"
              :label="$t('claims.FloorHeating')"
              :options="FloorHeatingOptions"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * FloorConstruction -->
          <Field
            v-slot="{ field, meta }"
            name="FloorConstruction"
            :rules="validateRequired"
            :model-value="FloorConstruction"
          >
            <SelectDropdown
              v-model="FloorConstruction"
              :label="$t('claims.FloorConstruction')"
              :options="FloorConstructionOptions"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * UnderFloorMaterial -->
          <Field
            v-slot="{ field, meta }"
            name="UnderFloorMaterial"
            :rules="validateRequired"
            :model-value="UnderFloorMaterial"
          >
            <SelectDropdown
              v-model="UnderFloorMaterial"
              :label="$t('claims.UnderFloorMaterial')"
              :options="UnderFloorMaterialOptions"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
          <!-- * * * * * * * * * * SubfloorMaterial -->
          <Field
            v-slot="{ field, meta }"
            name="SubfloorMaterial"
            :rules="validateRequired"
            :model-value="SubfloorMaterial"
          >
            <SelectDropdown
              v-model="SubfloorMaterial"
              :label="$t('claims.SubfloorMaterial')"
              :options="SubfloorMaterialOptions"
              :show-error="meta.validated && !meta.valid"
              :errors="meta.errors"
            />
          </Field>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * VaporBarierInstalled -->
          <div>
            <div class="type-sm text-text-subtle">{{ $t('claims.VaporBarierInstalled') }}</div>
            <Field
              v-slot="{ field, meta }"
              name="VaporBarierInstalled"
              :rules="validateRequired"
              :model-value="VaporBarierInstalled"
            >
              <div class="flex mt-12 gap-24">
                <label>
                  <input
                    v-model="VaporBarierInstalled"
                    type="radio"
                    value="true"
                  > {{ $t('claims.yes') }}
                </label>
                <label>
                  <input
                    v-model="VaporBarierInstalled"
                    type="radio"
                    value="false"
                  > {{ $t('claims.no') }}
                </label>
              </div>
              <div v-if="meta.errors.length" class="mt-8">
                <div
                  v-for="(error, idx) in meta.errors"
                  :key="idx"
                  class="flex flex-row items-center gap-4 whitespace-nowrap"
                >
                  <img
                    src="/icons/exclamation-circle-critical.svg"
                    class="w-16 h-16"
                    alt=""
                  >
                  <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
                </div>
              </div>
            </Field>
          </div>
          <!-- * * * * * * * * * * ExpansionGap -->
          <div>
            <div class="type-sm text-text-subtle">{{ $t('claims.ExpansionGap') }}</div>
            <Field
              v-slot="{ field, meta }"
              name="ExpansionGap"
              :rules="validateRequired"
              :model-value="ExpansionGap"
            >
              <div class="flex mt-12 gap-24">
                <label>
                  <input
                    v-model="ExpansionGap"
                    type="radio"
                    value="true"
                  > {{ $t('claims.yes') }}
                </label>
                <label>
                  <input
                    v-model="ExpansionGap"
                    type="radio"
                    value="false"
                  > {{ $t('claims.no') }}
                </label>
              </div>
              <div v-if="meta.errors.length" class="mt-8">
                <div
                  v-for="(error, idx) in meta.errors"
                  :key="idx"
                  class="flex flex-row items-center gap-4 whitespace-nowrap"
                >
                  <img
                    src="/icons/exclamation-circle-critical.svg"
                    class="w-16 h-16"
                    alt=""
                  >
                  <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
                </div>
              </div>
            </Field>
          </div>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * OilingPerformed -->
          <div>
            <div class="type-sm text-text-subtle">{{ $t('claims.OilingPerformed') }}</div>

            <div class="flex mt-12 gap-24">
              <label>
                <input
                  v-model="OilingPerformed"
                  type="radio"
                  value="true"
                > {{ $t('claims.yes') }}
              </label>
              <label>
                <input
                  v-model="OilingPerformed"
                  type="radio"
                  value="false"
                > {{ $t('claims.no') }}
              </label>
            </div>
          </div>
          <!-- * * * * * * * * * * EndJointOffset -->
          <div>
            <div class="type-sm text-text-subtle">{{ $t('claims.EndJointOffset') }}</div>
            <Field
              v-slot="{ field, meta }"
              name="EndJointOffset"
              :rules="validateRequired"
              :model-value="EndJointOffset"
            >
              <div class="flex mt-12 gap-24">
                <label>
                  <input
                    v-model="EndJointOffset"
                    type="radio"
                    value="true"
                  > {{ $t('claims.yes') }}
                </label>
                <label>
                  <input
                    v-model="EndJointOffset"
                    type="radio"
                    value="false"
                  > {{ $t('claims.no') }}
                </label>
              </div>
              <div v-if="meta.errors.length" class="mt-8">
                <div
                  v-for="(error, idx) in meta.errors"
                  :key="idx"
                  class="flex flex-row items-center gap-4 whitespace-nowrap"
                >
                  <img
                    src="/icons/exclamation-circle-critical.svg"
                    class="w-16 h-16"
                    alt=""
                  >
                  <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
                </div>
              </div>
            </Field>
          </div>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * HumidityInRange -->
          <div>
            <div class="type-sm text-text-subtle">{{ $t('claims.HumidityInRange') }}</div>
            <Field
              v-slot="{ field, meta }"
              name="HumidityInRange"
              :rules="validateRequired"
              :model-value="HumidityInRange"
            >
              <div class="flex mt-12 gap-24">
                <label>
                  <input
                    v-model="HumidityInRange"
                    type="radio"
                    value="true"
                  > {{ $t('claims.yes') }}
                </label>
                <label>
                  <input
                    v-model="HumidityInRange"
                    type="radio"
                    value="false"
                  > {{ $t('claims.no') }}
                </label>
              </div>
              <div v-if="meta.errors.length" class="mt-8">
                <div
                  v-for="(error, idx) in meta.errors"
                  :key="idx"
                  class="flex flex-row items-center gap-4 whitespace-nowrap"
                >
                  <img
                    src="/icons/exclamation-circle-critical.svg"
                    class="w-16 h-16"
                    alt=""
                  >
                  <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
                </div>
              </div>
            </Field>
          </div>
          <!-- * * * * * * * * * * UnderfloorTolerances -->
          <div>
            <div class="type-sm text-text-subtle">{{ $t('claims.UnderfloorTolerances') }}</div>
            <Field
              v-slot="{ field, meta }"
              name="UnderfloorTolerances"
              :rules="validateRequired"
              :model-value="UnderfloorTolerances"
            >
              <div class="flex mt-12 gap-24">
                <label>
                  <input
                    v-model="UnderfloorTolerances"
                    type="radio"
                    value="true"
                  > {{ $t('claims.yes') }}
                </label>
                <label>
                  <input
                    v-model="UnderfloorTolerances"
                    type="radio"
                    value="false"
                  > {{ $t('claims.no') }}
                </label>
              </div>
              <div v-if="meta.errors.length" class="mt-8">
                <div
                  v-for="(error, idx) in meta.errors"
                  :key="idx"
                  class="flex flex-row items-center gap-4 whitespace-nowrap"
                >
                  <img
                    src="/icons/exclamation-circle-critical.svg"
                    class="w-16 h-16"
                    alt=""
                  >
                  <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
                </div>
              </div>
            </Field>
          </div>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * ProfilingDate -->
          <div>
            <TextInput
              v-model="ProfilingDate"
              type="text"
              :label="$t('claims.ProfilingDate')"
            />
          </div>
          <!-- * * * * * * * * * * LabelNo -->
          <div>
            <TextInput
              v-model="LabelNo"
              type="text"
              :label="$t('claims.LabelNo')"
            />
          </div>
        </div>

        <div class="middleInstructions">
          <h2 class="type-headline-base uppercase">{{ $t('claims.AdditionalInformation') }}</h2>
        </div>

        <div class="inputHolder">
          <!-- * * * * * * * * * * AdditionalInformation -->
          <TextAreaInput v-model="AdditionalInformation" :label="$t('claims.AdditionalInformation')" />
          <div />
        </div>

        <!-- * * * * * * * * * * Files -->
        <div>
          <div class="middleInstructions wysiwyg">
            <ExtendedWysiwyg v-if="imageInstructions" :content="imageInstructions" />
          </div>
          <p v-if="attachedFiles.length" class="type-headline-base mb-12">Included file(s): {{ attachedFiles.length }}</p>
          <div v-if="attachedFiles.length" class="inputHolder items-end">
            <div
              v-for="(file, fi) in attachedFiles"
              :key="fi"
              class="h-full flex flex-col justify-between p-16 border-border-subtle border"
            >
              <div class="relative">
                <!-- Display image preview -->
                <img
                  v-if="file.isImage"
                  :src="file.preview"
                  class="min-h-64 max-h-320 mx-auto"
                  alt="File preview"
                >
                <!-- Display document icon and filename -->
                <div v-else class="min-h-64 flex flex-col items-center justify-center p-16 pb-0 rounded">
                  <img
                    src="/icons/file-send.svg"
                    class="w-48 h-48 mb-8"
                    alt="File"
                  >
                  <p class="type-sm text-center break-all px-8">{{ file.file.name }}</p>
                </div>
                <button
                  type="button"
                  class="flex items-center justify-center absolute top-0 right-0 h-24 w-24 bg-criticalPrimary rounded-full"
                  @click="removeFile(fi)"
                >
                  <img
                    src="/icons/close-inv.svg"
                    class="h-12 w-12"
                    alt=""
                  >
                </button>
              </div>
              <TextInput
                v-if="file.isImage"
                v-model="file.description"
                :label="$t('claims.image.addComment')"
                class="mt-12"
              />
            </div>
          </div>
          <p class="type-headline-base uppercase mb-12">{{ $t('claims.image.addImage') }}</p>
          <input
            ref="fileInput"
            type="file"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            multiple
            @change="handleFileChange"
          >
          <div v-if="imageValidationError" class="mt-8">
            <div class="flex flex-row items-center gap-4 whitespace-nowrap">
              <img
                src="/icons/exclamation-circle-critical.svg"
                class="w-16 h-16"
                alt=""
              >
              <div class="type-xs-medium leading-single text-criticalDark">{{ imageValidationError }}</div>
            </div>
          </div>
        </div>

        <!-- submit form -->
        <div class="flex justify-end">
          <button
            class="btn btn-lg mt-32"
            :class="{ loading }"
            type="submit"
          >{{ $t('claims.new.submit') }}</button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Field, Form } from 'vee-validate';
import TextInput from '~/components/form-elements/TextInput.vue';
import useValidation from '~/composables/useValidation';
import SelectDropdown from '~/components/form-elements/SelectDropdown.vue';
import TextAreaInput from '~/components/form-elements/TextAreaInput.vue';
import { useUserStore } from '~/store/user';
import useApiFetch from '~/composables/useApiFetch';
import { useUiStore } from '~/store/ui';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import type { ProductRaw } from '#constants/types/algolia';
import Product from '#models/product';
import { onClickOutside } from '@vueuse/core';
import ExtendedWysiwyg from '~/components/globals/ExtendedWysiwyg.vue';
import type { ISbRichtext } from 'storyblok-js-client';

const props = withDefaults(defineProps<{
  isPublic?: boolean;
  defaultExpanded?: boolean;
  imageInstructions?: ISbRichtext;
}>(), {
  isPublic: false,
  defaultExpanded: false,
  imageInstructions: undefined,
});

const { $t } = useNuxtApp();
const userStore = useUserStore();

const uiStore = useUiStore();

const emit = defineEmits<{
  (e: 'update'): void;
}>();

const {
  validateRequired,
  startValidate,
  validateEmail,
  validatePositiveNumber,
  validateDate,
} = useValidation();

const Customernumber= ref<string>(props.isPublic ? '' : (userStore.userProfile?.company?.code || '')); // req
const ContactName= ref<string>(props.isPublic ? '' : `${userStore.userProfile?.firstName || ''} ${userStore.userProfile?.lastName || ''}`.trim()); // req
const ContactEmail= ref<string>(props.isPublic ? '' : (userStore.userProfile?.email || '')); // req
const ContactPhone= ref<string>(props.isPublic ? '' : (userStore.userProfile?.phone || ''));
const ReportDate= ref<string>(''); // just called DATE in api
const OrderNumber= ref<string>(''); // req
const InstallationPerformedBy= ref<string>('');
const InstallationPerformedByOptions = $t('claims.InstallationPerformedBy.options').split(';').map(m => {
  return { key: m.trim(), title: m.trim() };
});

const InstallationType= ref<string | null>($t('claims.IT.option4').split(':')[1]);
const InstallationTypeOptions = [
  { key: $t('claims.IT.option1').split(':')[1], title: $t('claims.IT.option1').split(':')[0] },
  { key: $t('claims.IT.option2').split(':')[1], title: $t('claims.IT.option2').split(':')[0] },
  { key: $t('claims.IT.option3').split(':')[1], title: $t('claims.IT.option3').split(':')[0] },
  { key: $t('claims.IT.option4').split(':')[1], title: $t('claims.IT.option4').split(':')[0] },
];
const ConsumerName= ref<string>(''); // req
const ConsumerAddress= ref<string>(''); // req
const ConsumerPostcode= ref<string>(''); // req
const ConsumerCity= ref<string>(''); // req
const ConsumerCellphone= ref<string>(''); // req
const ConsumerEmail= ref<string>('');
const ConsumerDeliveryInstructions= ref<string>('');
const DateOnRecipt= ref<string>('');
// we need to use a string for true/false because wee validate
const Receipt= ref<string | null>(null); // req
const CustomerClaimNo= ref<string>('');
const MainReasonOption= ref<string>(''); // req
const MainReasonOptions = [
  { key: $t('claims.MR.option1').split(':')[1], title: $t('claims.MR.option1').split(':')[0] },
  { key: $t('claims.MR.option2').split(':')[1], title: $t('claims.MR.option2').split(':')[0] },
  { key: $t('claims.MR.option3').split(':')[1], title: $t('claims.MR.option3').split(':')[0] },
  { key: $t('claims.MR.option4').split(':')[1], title: $t('claims.MR.option4').split(':')[0] },
  { key: $t('claims.MR.option5').split(':')[1], title: $t('claims.MR.option5').split(':')[0] },
  { key: $t('claims.MR.option6').split(':')[1], title: $t('claims.MR.option6').split(':')[0] },
  { key: $t('claims.MR.option7').split(':')[1], title: $t('claims.MR.option7').split(':')[0] },
  { key: $t('claims.MR.option8').split(':')[1], title: $t('claims.MR.option8').split(':')[0] },
  { key: $t('claims.MR.option9').split(':')[1], title: $t('claims.MR.option9').split(':')[0] },
  { key: $t('claims.MR.option10').split(':')[1], title: $t('claims.MR.option10').split(':')[0] },
  { key: $t('claims.MR.option11').split(':')[1], title: $t('claims.MR.option11').split(':')[0] },
  { key: $t('claims.MR.option12').split(':')[1], title: $t('claims.MR.option12').split(':')[0] },
  { key: $t('claims.MR.option13').split(':')[1], title: $t('claims.MR.option13').split(':')[0] },
];
const ClaimError= ref<string>(''); // req
const ArticleNumber= ref<string>(''); // req
const ProductName= ref<string>('');
const DeliveredQuantity= ref<number | null>(null); // req
const ClaimedQuantity= ref<number | null>(null); // req
const InstallationDate= ref<string>(''); // req
const DefectDiscovered= ref<string>(''); // req
const RoomType= ref<string[]>([]);
const FloorHeating= ref<string>(''); // req
const FloorHeatingOptions = $t('claims.FloorHeating.options').split(';').map(m => {
  return { key: m.trim(), title: m.trim() };
});
const FloorConstruction= ref<string>(''); // req
const FloorConstructionOptions = $t('claims.FloorConstruction.options').split(';').map(m => {
  return { key: m.trim(), title: m.trim() };
});

const UnderFloorMaterial= ref<string>(''); // req
const UnderFloorMaterialOptions = $t('claims.UnderFloorMaterial.options').split(';').map(m => {
  return { key: m.trim(), title: m.trim() };
});

const SubfloorMaterial= ref<string>(''); // req
const SubfloorMaterialOptions = $t('claims.SubfloorMaterial.options').split(';').map(m => {
  return { key: m.trim(), title: m.trim() };
});
const VaporBarierInstalled= ref<string | null>(null); // req
const ExpansionGap= ref<string | null>(null); // req
const OilingPerformed= ref<string | null>(null);
const EndJointOffset= ref<string | null>(null); // req
const ProfilingDate= ref<string>('');
const LabelNo= ref<string>('');
const HumidityInRange= ref<string | null>(null); // req
const UnderfloorTolerances= ref<string | null>(null); // req
const AdditionalInformation= ref<string>('');

const loading = ref(false);
const { apiPost, handleApiError } = useApiFetch();

const submitForm = async() => {
  console.log('submitted without errors');

  // Validate minimum 2 images are uploaded
  if (attachedFiles.value.length < 2) {
    imageValidationError.value = $t('claims.image.required');
    loading.value = false;
    return;
  }

  imageValidationError.value = '';
  loading.value = true;

  const formData = new FormData();

  const Description = `InstallationPerformedBy: ${InstallationPerformedBy.value}` + '\r\n'
  + `DateOnRecipt: ${DateOnRecipt.value}` + '\r\n'
  + `DefectDiscovered: ${DefectDiscovered.value}` + '\r\n'
  + `RoomType: ${RoomType.value}` + '\r\n'
  + `FloorHeating: ${FloorHeating.value}` + '\r\n'
  + `FloorConstruction: ${FloorConstruction.value}` + '\r\n'
  + `UnderFloorMaterial: ${UnderFloorMaterial.value}` + '\r\n'
  + `SubfloorMaterial: ${SubfloorMaterial.value}` + '\r\n'
  + `VaporBarierInstalled: ${VaporBarierInstalled.value}` + '\r\n'
  + `ExpansionGap: ${ExpansionGap.value}` + '\r\n'
  + `OilingPerformed: ${OilingPerformed.value}` + '\r\n'
  + `EndJointOffset: ${EndJointOffset.value}` + '\r\n'
  + `ProfilingDate: ${ProfilingDate.value}` + '\r\n'
  + `HumidityInRange: ${HumidityInRange.value}` + '\r\n'
  + `UnderfloorTolerances: ${UnderfloorTolerances.value}` + '\r\n'
  + `AdditionalInformation: ${AdditionalInformation.value}` + '\r\n';

  formData.append('Customernumber', Customernumber.value);
  formData.append('ContactName', ContactName.value);
  formData.append('ContactEmail', ContactEmail.value);
  formData.append('ContactPhone', ContactPhone.value);
  formData.append('OrderNumber', OrderNumber.value);
  formData.append('InstallationType', InstallationType.value || '');
  formData.append('ConsumerName', ConsumerName.value);
  formData.append('ConsumerAddress', ConsumerAddress.value);
  formData.append('ConsumerPostcode', ConsumerPostcode.value);
  formData.append('ConsumerCity', ConsumerCity.value);
  formData.append('ConsumerCellphone', ConsumerCellphone.value);
  formData.append('ConsumerEmail', ConsumerEmail.value);
  formData.append('ConsumerDeliveryInstructions', ConsumerDeliveryInstructions.value);
  formData.append('Receipt', Receipt.value || '');
  formData.append('CustomerClaimNo', CustomerClaimNo.value);
  formData.append('MainReasonOption', MainReasonOption.value);
  formData.append('ClaimError', ClaimError.value);
  formData.append('Product', ArticleNumber.value); // both ArticleNumber adn ProductName listed as product
  formData.append('DeliveredQuantity', DeliveredQuantity.value + '');
  formData.append('ClaimedQuantity', ClaimedQuantity.value + '');
  formData.append('InstallationDate', InstallationDate.value);
  formData.append('LabelNo', LabelNo.value);
  formData.append('Description', Description);

  attachedFiles.value.forEach((fileObj) => {
    formData.append('File[]', fileObj.file);
    formData.append('FileDescription[]', fileObj.description);
  });

  const res = await apiPost('/cases', formData);

  if (res) {
    console.log(res);
    uiStore.setTemporarySuccessMsg($t('claims.new.success'));
    expanded.value = false;

    setTimeout(()=> {
      emit('update');
    }, 5000);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

  } else {
    handleApiError();
  }
  loading.value = false;

};

type UploadedFiles = {
  file: File,
  preview: string,
  description: string,
  isImage: boolean,
}

const attachedFiles = ref<UploadedFiles[]>([]);
const fileInput = useTemplateRef('fileInput');
const fileError = ref(false);
const imageValidationError = ref('');

// Article search state
const articleSearchActive = ref(false);
const articleSearchResults = ref<any[]>([]);
const articleSearchLoading = ref(false);
const selectedArticleIndex = ref(-1);
const selectedProductName = ref('');
const articleSearchTimer = ref<any>(null);
const articleResultsDiv = ref<HTMLElement>();
const articleSearchDiv = ref<HTMLElement>();
const { freeTextSearch } = useAlgoliaFetch();

// Close article search when clicking outside
onClickOutside(articleSearchDiv, () => {
  closeArticleSearch();
});

const handleFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files) { return; }
  fileError.value = false;

  const uploadedFiles = Array.from(input.files);
  uploadedFiles.forEach((file) => {
    const isImage = file.type.startsWith('image/');
    attachedFiles.value.push({
      file: file,
      preview: isImage ? URL.createObjectURL(file) : '',
      description: '',
      isImage,
    });
  });

  if (fileInput.value) {
    fileInput.value.value = null;
  }
};

const removeFile = (index: number) => {
  // Revoke the object URL to prevent memory leaks (only for images)
  if (attachedFiles.value[index].isImage && attachedFiles.value[index].preview) {
    URL.revokeObjectURL(attachedFiles.value[index].preview);
  }
  // Remove the file from the array
  attachedFiles.value.splice(index, 1);
  if (attachedFiles.value.length >= 2) {
    imageValidationError.value = '';
  }
};

// Article search functions
const searchArticles = () => {
  clearTimeout(articleSearchTimer.value);
  articleSearchTimer.value = setTimeout(async() => {
    if (ArticleNumber.value.length < 2) {
      articleSearchResults.value = [];
      return;
    }

    articleSearchLoading.value = true;
    const res = await freeTextSearch<ProductRaw[]>(
      ArticleNumber.value,
      10,
      '_search',
      true,
      false
    );

    if (res) {
      articleSearchResults.value = res.hits.map((hit: ProductRaw) => {
        const product = Product.create(hit, true);
        return {
          partNo: product.partNo,
          name: product.variantName,
        };
      });
    }
    articleSearchLoading.value = false;
    selectedArticleIndex.value = -1;
  }, 300);
};

const selectArticle = (product: any) => {
  ArticleNumber.value = product.partNo;
  selectedProductName.value = product.name;
  closeArticleSearch();
};

const selectNextArticle = (direction: number) => {
  const newVal = selectedArticleIndex.value + direction;
  if (newVal < 0) {
    selectedArticleIndex.value = 0;
  } else if (newVal >= articleSearchResults.value.length) {
    selectedArticleIndex.value = articleSearchResults.value.length - 1;
  } else {
    selectedArticleIndex.value = newVal;
  }

  // Scroll to selected item
  nextTick(() => {
    const children = articleResultsDiv.value?.children as HTMLDivElement[] | undefined;
    if (children && children[selectedArticleIndex.value]) {
      children[selectedArticleIndex.value].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });
};

const selectCurrentArticle = () => {
  if (selectedArticleIndex.value >= 0 && selectedArticleIndex.value < articleSearchResults.value.length) {
    selectArticle(articleSearchResults.value[selectedArticleIndex.value]);
  }
};

const closeArticleSearch = () => {
  articleSearchActive.value = false;
  articleSearchResults.value = [];
  selectedArticleIndex.value = -1;
};

const expanded = ref(false);

// Customer verification for public mode
const customerVerified = ref(false);
const verifyingCustomer = ref(false);
const customerLookupError = ref('');
const companyName = ref('');

const verifyCustomerNumber = async() => {
  if (!Customernumber.value || Customernumber.value.trim() === '') {
    customerLookupError.value = $t('claims.customerNumberRequired');
    return;
  }

  verifyingCustomer.value = true;
  customerLookupError.value = '';

  const res = await apiPost('/cases/validate-customer-number', { customerNumber: Customernumber.value });

  if (res && res.exists) {
    customerVerified.value = true;
    customerLookupError.value = '';
    companyName.value = res.companyName || '';
  } else {
    customerLookupError.value = $t('claims.customerNotFound');
  }

  verifyingCustomer.value = false;
};

// Just fills the for for testing purposes
const testFillRequired = () => {
  ContactEmail.value = 'testcontact@test.se';
  OrderNumber.value = 'Test orderno';
  ConsumerName.value = 'Test consumer name';
  ConsumerAddress.value = 'Test Consumer Address';
  ConsumerPostcode.value = '12345';
  ConsumerCity.value = 'Test Stad';
  ConsumerCellphone.value = 'Test consumer phone';
  Receipt.value = 'true';
  MainReasonOption.value = '140100022';
  ClaimError.value = 'Test ClaimEroor';
  ArticleNumber.value = '151NCSEK05KW240';
  DeliveredQuantity.value = 105;
  ClaimedQuantity.value = 50;
  InstallationDate.value = '2024-10-10';
  DefectDiscovered.value = '2024-12-12';
  FloorHeating.value = FloorHeatingOptions[0]?.key || '';
  FloorConstruction.value = FloorConstructionOptions[0]?.key || '';
  UnderFloorMaterial.value = UnderFloorMaterialOptions[0]?.key || '';
  SubfloorMaterial.value = SubfloorMaterialOptions[0]?.key || '';
  VaporBarierInstalled.value = 'false';
  ExpansionGap.value = 'true';
  EndJointOffset.value = 'true';
  HumidityInRange.value = 'false';
  UnderfloorTolerances.value = 'true';
};

</script>

<style scoped lang="postcss">
.inputHolder {
  @apply flex gap-24 mb-24;
  >div {
    @apply basis-1/2;
  }
}
.middleInstructions {
  @apply py-32;
  h2, p {
    @apply mb-8;
  }
}
</style>
